import { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import axios from "axios";

// Layout
import Layout from "./components/Layout";
import AdminLayout from "./components/AdminLayout";

// Pages
import Home from "./pages/Home";
import About from "./pages/About";
import Projects from "./pages/Projects";
import Tools from "./pages/Tools";
import ToolCopropriete from "./pages/ToolCopropriete";
import ToolCalculateur from "./pages/ToolCalculateur";
import ToolFaisabilite from "./pages/ToolFaisabilite";
import CityLanding from "./pages/CityLanding";
import Blog from "./pages/Blog";
import BlogPost from "./pages/BlogPost";
import FAQ from "./pages/FAQ";
import Videos from "./pages/Videos";
import Contact from "./pages/Contact";
import DivisionImmobiliere from "./pages/DivisionImmobiliere";

// Admin Pages
import AdminLogin from "./pages/admin/AdminLogin";
import AdminDashboard from "./pages/admin/AdminDashboard";
import AdminBlogs from "./pages/admin/AdminBlogs";
import AdminBlogEdit from "./pages/admin/AdminBlogEdit";
import AdminMessages from "./pages/admin/AdminMessages";
import AdminProperties from "./pages/admin/AdminProperties";
import Properties from "./pages/Properties";
import PropertyDetail from "./pages/PropertyDetail";

// Auth
import { AuthProvider, useAuth } from "./context/AuthContext";
import ScrollToTop from "./components/ScrollToTop";
import { Toaster } from "./components/ui/sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-[#1e3a5f] border-t-transparent rounded-full loading-spinner"></div>
      </div>
    );
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/admin/login" replace />;
  }
  
  return children;
};

function AppRoutes() {
  // Seed data on first load
  useEffect(() => {
    const seedData = async () => {
      try {
        await axios.post(`${API}/seed`);
      } catch (e) {
        console.log("Seed data already exists or error:", e.message);
      }
    };
    seedData();
  }, []);

  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/" element={<Layout />}>
        <Route index element={<Home />} />
        <Route path="qui-sommes-nous" element={<About />} />
        <Route path="division-immobiliere" element={<DivisionImmobiliere />} />
        <Route path="nos-biens" element={<Properties />} />
        <Route path="nos-biens/:id" element={<PropertyDetail />} />
        <Route path="nos-outils" element={<Tools />} />
        <Route path="nos-outils/gestion-copropriete" element={<ToolCopropriete />} />
        <Route path="nos-outils/calculateur-rentabilite" element={<ToolCalculateur />} />
        <Route path="nos-outils/diagnostic-faisabilite-division" element={<ToolFaisabilite />} />
        <Route path="marchand-de-biens-paris" element={<CityLanding city="paris" />} />
        <Route path="marchand-de-biens-lyon" element={<CityLanding city="lyon" />} />
        <Route path="marchand-de-biens-chambery" element={<CityLanding city="chambery" />} />
        <Route path="marchand-de-biens-annecy" element={<CityLanding city="annecy" />} />
        <Route path="blog" element={<Blog />} />
        <Route path="blog/:slug" element={<BlogPost />} />
        <Route path="faq" element={<FAQ />} />
        <Route path="videos" element={<Videos />} />
        <Route path="contact" element={<Contact />} />
      </Route>

      {/* Admin Routes */}
      <Route path="/admin/login" element={<AdminLogin />} />
      <Route
        path="/admin"
        element={
          <ProtectedRoute>
            <AdminLayout />
          </ProtectedRoute>
        }
      >
        <Route index element={<AdminDashboard />} />
        <Route path="blogs" element={<AdminBlogs />} />
        <Route path="blogs/new" element={<AdminBlogEdit />} />
        <Route path="blogs/edit/:id" element={<AdminBlogEdit />} />
        <Route path="messages" element={<AdminMessages />} />
        <Route path="biens" element={<AdminProperties />} />
      </Route>

      {/* Catch all */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <HelmetProvider>
      <div className="App">
        <BrowserRouter>
          <ScrollToTop />
          <AuthProvider>
            <AppRoutes />
            <Toaster richColors position="top-right" />
          </AuthProvider>
        </BrowserRouter>
      </div>
    </HelmetProvider>
  );
}

export default App;
